import { useCallback, useEffect, useState, useRef } from 'react';

import { isCancelRequest } from '../configs/axios';

const defaultMerge = (response, prevData, params) => {
  if (params && params.page > 1) {
    return {
      ...response,
      data: [...prevData.data, ...response.data],
    };
  }

  return response;
};

const defaultTransformResponse = (response) => response.data;
const defaultTransformErrorResponse = (response) => response;

function useApiQuery(
  request,
  {
    params,
    skip = false,
    cancelable = true,
    merge = defaultMerge, // Should be memoized
    transformResponse = defaultTransformResponse, // Should be memoized
    transformErrorResponse = defaultTransformErrorResponse, // Should be memoized
  } = {}
) {
  const paramsFlag = JSON.stringify(params);
  const [refetchFlag, setRefetchFlag] = useState();

  const [data, setData] = useState();
  const [error, setError] = useState();
  const [isLoading, setLoading] = useState(!skip);

  // States
  const isFirstLoadingRef = useRef(true);
  const isSuccessRef = useRef(false);
  const isErrorRef = useRef(false);

  const originalArgsRef = useRef();

  // Request cancelation
  const controllerRef = useRef();
  const cancelRequest = useCallback(() => {
    if (controllerRef.current) controllerRef.current.abort();
  }, []);

  const handleRequest = (requestParams) => {
    isSuccessRef.current = false;
    isErrorRef.current = false;

    setLoading(true);

    let abortSignal = {};

    if (cancelable) {
      cancelRequest();

      controllerRef.current = new AbortController();
      abortSignal = { signal: controllerRef.current.signal };
    }

    originalArgsRef.current = requestParams;

    request({
      params: requestParams,
      ...abortSignal,
    })
      .then((response) => {
        isFirstLoadingRef.current = false;
        isSuccessRef.current = true;

        setData((prevData) => merge(transformResponse(response), prevData, requestParams));
        setLoading(false);
      })
      .catch((err) => {
        if (!isCancelRequest(err)) {
          isErrorRef.current = true;

          setError(transformErrorResponse(err.response));
          setLoading(false);
        }
      });
  };

  const handleRefetch = useCallback(() => {
    setRefetchFlag((prevState) => (prevState ? prevState + 1 : 1));
  }, []);

  // Refetch effect
  useEffect(() => {
    if (refetchFlag) {
      handleRequest(params);
    }

    return cancelRequest;
  }, [refetchFlag]);

  useEffect(() => {
    if (!skip) {
      handleRequest(params);
    }

    return cancelRequest;
  }, [skip, paramsFlag]);

  return {
    data,
    error,
    originalArgs: originalArgsRef.current, // Arguments originally passed to the request
    isLoading, // Query is currently fetching, but might have data from an earlier request.
    isFirstLoading: isFirstLoadingRef.current, // Query is currently loading for the first time.
    isSuccess: isSuccessRef.current, // Query has data from a successful request.
    isError: isErrorRef.current, // Query is in an error state.

    refetch: handleRefetch, // A function to force refetch the query
    updateData: setData,
  };
}

export default useApiQuery;
